import { type DocumentReference, Timestamp } from 'firebase/firestore';

import { EStatus, type IMetadata, type IObjectId } from './common.type';
import {
  type CNsPrice,
  DEFAULT_PRICE_VALUES,
  ECurrency,
  type IPrice,
} from './price.type';

export const CSubscriptionsPath = 'subscriptions';

export enum ESubscriptionType {
  BASIC = 'Basic',
  PREMIUM = 'Premium',
  STANDARD = 'Standard',
}

export interface ISubscription {
  id: IObjectId['id'];
  title: string;
  description: string;
  tags: string[];
  metadata: IMetadata;
  [CNsPrice]: IPrice;
  isTrialed: boolean;
  assignedFeatures: DocumentReference[];
  type: keyof typeof ESubscriptionType;
  status: EStatus;
  /**
   * Credits granted per 30-day period to wallets on tenants that hold this
   * subscription. Consumed by the wallet reconcile flow; 0 = no credit grant.
   */
  monthlyCredits: number;
  /**
   * Materialized billing-period bounds, written by a future renewal/billing
   * webhook. Absent today: the entitlements adapter derives the period from
   * `metadata.createdAt + price.duration` until these are populated
   * (`stored ?? derived`). Seeded here so the billing path (B) is a non-breaking
   * field write, not a model change.
   */
  currentPeriodStart?: Timestamp;
  currentPeriodEnd?: Timestamp;
  /** Payment provider that owns the billing lifecycle, once real billing lands. */
  provider?: string;
}

export const DEFAULT_SUBSCRIPTION: Partial<ISubscription> = {
  title: '',
  description: '',
  tags: [],
  type: 'BASIC',
  assignedFeatures: [],
  isTrialed: false,
  status: EStatus.PROCESSING,
  monthlyCredits: 0,
  ...DEFAULT_PRICE_VALUES(ECurrency.USD, false),
  metadata: {
    createdAt: Timestamp.fromDate(new Date()),
    updatedAt: Timestamp.fromDate(new Date()),
  },
};
